import React from "react";

// Typographic blog cover — replaces external image URLs.
// Renders the title inside a bold brutalist grid with the category, date and cover number.
// `variant`: "light" = paper bg + ink text + accent, "dark" = ink bg + paper text + accent

export default function BlogCover({ post, variant = "auto", compact = false }) {
  const isDark = variant === "dark" || (variant === "auto" && post.toneDark);
  const accent = post.accent || "#E63946";

  const bg = isDark ? "#0A0A0A" : "#FDFDFD";
  const ink = isDark ? "#FDFDFD" : "#0A0A0A";
  const ink2 = isDark ? "rgba(253,253,253,0.55)" : "rgba(10,10,10,0.55)";

  // Scale the cover title down for longer headlines so it never overflows the fixed cover box.
  const len = post.title.length;
  const base = compact ? 26 : 38;
  let titleSize = base;
  if (len > 85) titleSize = compact ? 17 : 25;
  else if (len > 60) titleSize = compact ? 20 : 30;
  else if (len > 42) titleSize = compact ? 23 : 34;

  return (
    <div
      aria-label={post.title}
      className="relative w-full h-full overflow-hidden"
      style={{ background: bg, color: ink }}
      data-testid={`blog-cover-${post.slug}`}
    >
      {/* Accent slab */}
      <div
        className="absolute right-0 top-0 h-full"
        style={{ width: "28%", background: accent, mixBlendMode: isDark ? "screen" : "multiply" }}
      />
      {/* Grain / texture using SVG noise */}
      <svg
        className="absolute inset-0 w-full h-full pointer-events-none"
        aria-hidden="true"
        style={{ opacity: 0.08, mixBlendMode: "multiply" }}
      >
        <filter id={`grain-${post.slug}`}>
          <feTurbulence type="fractalNoise" baseFrequency="0.9" numOctaves="2" stitchTiles="stitch" />
          <feColorMatrix type="matrix" values="0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 0 0.6 0" />
        </filter>
        <rect width="100%" height="100%" filter={`url(#grain-${post.slug})`} />
      </svg>

      {/* Top row */}
      <div className="absolute top-0 inset-x-0 flex items-center justify-between px-4 md:px-6 py-3 md:py-4">
        <span
          className="font-mono uppercase tracking-[0.22em]"
          style={{ fontSize: compact ? 9 : 10, color: ink }}
        >
          ◆ The Digital Marketer
        </span>
        <span
          className="font-mono uppercase tracking-[0.22em]"
          style={{ fontSize: compact ? 9 : 10, color: ink2 }}
        >
          {post.date}
        </span>
      </div>

      {/* Main title — absolutely scaled for card cover */}
      <div className="absolute inset-0 flex flex-col justify-end px-4 md:px-6 pb-10 md:pb-14 pt-12 md:pt-16">
        <div className="flex flex-wrap gap-2 mb-3">
          {post.categories.slice(0, 2).map((c) => (
            <span
              key={c}
              className="font-mono uppercase tracking-[0.2em] px-2 py-1"
              style={{
                fontSize: compact ? 9 : 10,
                background: ink,
                color: bg,
              }}
            >
              {c}
            </span>
          ))}
        </div>
        <h3
          className="font-heading font-black tracking-tightest leading-[0.92] overflow-hidden"
          style={{
            fontSize: titleSize,
            color: ink,
            display: "-webkit-box",
            WebkitLineClamp: 5,
            WebkitBoxOrient: "vertical",
          }}
        >
          {post.title}
        </h3>
      </div>

      {/* Bottom row */}
      <div
        className="absolute bottom-0 inset-x-0 px-4 md:px-6 py-3 md:py-4 flex items-center justify-between border-t"
        style={{ borderColor: isDark ? "rgba(253,253,253,0.15)" : "rgba(10,10,10,0.12)" }}
      >
        <span
          className="font-mono uppercase tracking-[0.22em]"
          style={{ fontSize: compact ? 9 : 10, color: ink2 }}
        >
          Essay · {post.readTime}
        </span>
        <span
          className="font-heading font-black tracking-tightest"
          style={{ fontSize: compact ? 14 : 18, color: accent }}
        >
          /{String((post.index ?? 0) + 1).padStart(2, "0")}
        </span>
      </div>
    </div>
  );
}
